<script setup lang="ts">
import { UnorderedListOutlined } from '@ant-design/icons-vue';
import { gql } from 'graphql-tag';
import { ref } from 'vue';
import { GqlQuery } from '~/lib/fw';
import Dashboard from '~/pages/area-trabalho/Dashboard.vue';
import { useAuthStore } from '~/stores/auth';
import { useDrawerStore } from '~/stores/drawer';
import { formatCnj } from '~/utils';

const authStore = useAuthStore();
const drawerStore = useDrawerStore();
const pastasPendenciasRef = ref(null);

const compromissosGqlDatatableVars = {
  where: {
    and: [
      {
        responsavelCodigo: {
          in: [authStore.codigoUsuario],
        },
      },
    ],
  },
};

const pastasComPendenciasGqlDatatableVars = {
  where: {
    and: [
      {
        fluxos: {
          some: {
            compromissos: {
              some: {
                status: {
                  eq: 1,
                },
                responsavelCodigo: {
                  in: [authStore.codigoUsuario],
                },
              },
            },
          },
        },
      },
    ],
  },
};

function onPastaClick(record: any) {
  drawerStore.push2({
    title: 'COMPROMISSOS PENDENTES DA PASTA',
    wrapperStyle: {
      paddingLeft: '8px',
      paddingRight: '8px',
    },
    componentName: 'CompromissoGqlDatatable',
    params: {
      enableRowSelection: true,
      showStatusSearchBar: false,
      variables: {
        where: {
          and: [
            {
              responsavelCodigo: {
                eq: authStore.codigoUsuario,
              },
            },
            {
              status: {
                eq: 1,
              },
            },
            {
              fluxo: {
                pastaCodigo: {
                  eq: record.codigo,
                },
              },
            },
          ],
        },
      },
    },
    onClose: async () => {
      await (pastasPendenciasRef.value as any)?.reloadAsync();
    },
  });
}

const usuarioGql = gql`
  query usuarioQuery($usuarioCodigo: String!) {
    cache(where: { partitionKey: { eq: $usuarioCodigo } }) {
      key
      value
    }
  }
`;
</script>

<template>
  <Page :loading="false" menu="area-trabalho">
    <div class="area-trabalho-page" style="padding-bottom: 24px">
      <a-row justify="center">
        <a-col :xs="24" :md="24" :lg="24" :xl="24">
          <a-row>
            <a-col :span="12">
              <h1>
                <UnorderedListOutlined
                  style="color: #bebcbc; font-size: 18px"
                />
                Área de trabalho
              </h1>
            </a-col>
            <a-col :span="12" />
          </a-row>

          <a-tabs
            style="margin-top: 8px"
            :animated="false"
            :destroy-inactive-tab-pane="true"
          >
            <a-tab-pane key="1" tab="Geral">
              <GqlQuery
                :query-key="[authStore.codigoUsuario]"
                :query="usuarioGql"
                :variables="{ usuarioCodigo: authStore.codigoUsuario }"
                :retry="false"
                :remove-cache-on-unmount="true"
              >
                <template #loading>
                  <GestaoLoadingSkeleton />
                </template>
                <template #default="{ data, isFetching }">
                  <a-spin :spinning="isFetching">
                    <Dashboard
                      :responsavel="authStore.codigoUsuario!"
                      :data="data.cache"
                    />
                  </a-spin>
                </template>
              </GqlQuery>
            </a-tab-pane>
            <a-tab-pane key="2" tab="Compromissos">
              <CompromissoGqlDatatable
                :enable-row-selection="true"
                :show-text-search="true"
                :variables="compromissosGqlDatatableVars"
              />
            </a-tab-pane>
            <a-tab-pane key="3" tab="Pastas com pendências">
              <PastaGqlDatatable
                ref="pastasPendenciasRef"
                :variables="pastasComPendenciasGqlDatatableVars"
              >
                <template #nomeColumn="{ record }">
                  <a-row>
                    <a-col :span="24">
                      <a-row>
                        <a-col>
                          <a-tooltip>
                            <template #title>
                              Ver compromissos pendentes
                            </template>
                            <a
                              href="#"
                              @click.prevent="() => onPastaClick(record)"
                            >
                              {{ record.nome }}
                            </a>
                          </a-tooltip>
                        </a-col>
                      </a-row>
                      <a-row>
                        <a-col>
                          <div style="margin-top: 4px">
                            <a-typography-text
                              v-if="record.numeroCnj"
                              style="color: #7d7c7c"
                              copyable
                            >
                              {{ formatCnj(record.numeroCnj) }}
                            </a-typography-text>
                          </div>
                        </a-col>
                      </a-row>
                    </a-col>
                  </a-row>
                </template>
              </PastaGqlDatatable>
            </a-tab-pane>
            <a-tab-pane key="4" tab="Conclusões de fluxos pendentes">
              <CompromissoGqlDatatable
                :enable-row-selection="true"
                :show-status-search-bar="false"
                :variables="{
                  where: {
                    and: [
                      {
                        responsavelCodigo: {
                          eq: authStore.codigoUsuario,
                        },
                      },
                      {
                        status: {
                          eq: 1,
                        },
                      },
                      {
                        tipo: {
                          eq: 71,
                        },
                      },
                    ],
                  },
                }"
              />
            </a-tab-pane>
          </a-tabs>
        </a-col>
      </a-row>
    </div>
  </Page>
</template>
